/**
 * ErrorBoundary — React error boundary with reset support.
 *
 * Simple React error boundary with reset support for React Query.
 */
import React, { Component, type ErrorInfo, type ReactNode } from "react"

export interface ErrorFallbackProps {
  error: Error & { statusCode?: number }
  resetErrorBoundary: () => void
}

interface ErrorBoundaryProps {
  FallbackComponent?: React.ComponentType<ErrorFallbackProps>
  fallback?: ReactNode
  fallbackRender?: (props: ErrorFallbackProps) => ReactNode
  onReset?: () => void
  onError?: (error: Error, info: ErrorInfo) => void
  resetKeys?: unknown[]
  children: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // resetKeys is typically an inline array (new reference each render), so
    // compare element-wise instead of by reference.
    const prevKeys = prevProps.resetKeys ?? []
    const nextKeys = this.props.resetKeys ?? []
    const keysChanged =
      prevKeys.length !== nextKeys.length ||
      prevKeys.some((key, index) => !Object.is(key, nextKeys[index]))
    if (this.state.error && keysChanged) {
      this.resetErrorBoundary()
    }
  }

  resetErrorBoundary = () => {
    this.props.onReset?.()
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    const { FallbackComponent, fallback, fallbackRender, children } = this.props

    if (error) {
      const fallbackProps: ErrorFallbackProps = {
        error: error as Error & { statusCode?: number },
        resetErrorBoundary: this.resetErrorBoundary,
      }
      if (FallbackComponent) return <FallbackComponent {...fallbackProps} />
      if (fallbackRender) return <>{fallbackRender(fallbackProps)}</>
      if (fallback !== undefined) return fallback
      return null
    }

    return children
  }
}

export { ErrorBoundary as default }
